import React, { createContext, useState, useEffect } from 'react';
import { useMutation } from 'react-apollo';
import { IN_STORE_USER_REGISTER } from '../services/dataServices';
import { setCookie, getCookie, removeCookie } from '../services/cookies';
import { trackEvent } from '../services/analytics';

export const InStoreContext = createContext();

export const InStoreContextProvider = ({ children }) => {
  // Check if we're in in-store mode from localStorage or current path
  const [isInStoreMode, setIsInStoreMode] = useState(() => {
    const storedMode = localStorage.getItem('isInStoreMode');
    const currentPath = window.location.pathname;
    // If we're on /at-home, force in-store mode to be false
    if (currentPath === '/at-home') {
      localStorage.removeItem('isInStoreMode');
      return false;
    }
    return storedMode === 'true' || currentPath === '/in-store';
  });
  
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const [hasStartedConsultation, setHasStartedConsultation] = useState(() => {
    return localStorage.getItem('hasStartedConsultation') === 'true';
  });

  const [registerInStoreUser] = useMutation(IN_STORE_USER_REGISTER, {
    onCompleted: res => {
      if (res?.user_registerInStoreCustomer?.token) {
        setCookie('token', res.user_registerInStoreCustomer.token, 30 * 60);
        setIsUserRegistered(true);
        trackEvent('in_store_user_registered');
      }
    },
  });

  // Toggle body class for in-store mode CSS overrides
  useEffect(() => {
    if (isInStoreMode) {
      document.body.classList.add('in-store-mode');
    } else {
      document.body.classList.remove('in-store-mode');
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('in-store-mode');
    };
  }, [isInStoreMode]);

  useEffect(() => {
    // Check if we should activate in-store mode
    const currentPath = window.location.pathname;
    const storedMode = localStorage.getItem('isInStoreMode');
    
    if (currentPath === '/at-home') {
      // Always clear in-store mode when on /at-home
      clearInStoreMode();
    } else if (currentPath === '/in-store' || storedMode === 'true') {
      setIsInStoreMode(true);
      localStorage.setItem('isInStoreMode', 'true');
      
      // Only register user if we haven't already and don't have a token
      if (!getCookie('token') && !isUserRegistered) {
        registerInStoreUser();
      } else if (getCookie('token')) {
        setIsUserRegistered(true);
      }
    }
  }, [registerInStoreUser, isUserRegistered]);

  // Track when consultation starts by monitoring the consultationStarted cookie
  useEffect(() => {
    if (isInStoreMode) {
      const checkConsultationStatus = () => {
        const consultationStarted = getCookie('consultationStarted');
        if (consultationStarted && !hasStartedConsultation) {
          setHasStartedConsultation(true);
          localStorage.setItem('hasStartedConsultation', 'true');
        }
      };

      // Check immediately
      checkConsultationStatus();

      // Set up an interval to check periodically (in case cookie is set after this component mounts)
      const interval = setInterval(checkConsultationStatus, 1000);

      return () => clearInterval(interval);
    }
  }, [isInStoreMode, hasStartedConsultation]);

  // Function to clear in-store mode (for testing or when leaving store)
  const clearInStoreMode = () => {
    setIsInStoreMode(false);
    setHasStartedConsultation(false);
    
    // Only clear in-store specific flags
    localStorage.removeItem('isInStoreMode');
    localStorage.removeItem('hasStartedConsultation');
  };

  // Function to reset user session and create new in-store customer
  const resetInStoreSession = () => {
    // Clear all cookies and tokens
    removeCookie('token');
    removeCookie('guestToken');
    removeCookie('consultationStarted');
    
    // Clear localStorage data
    localStorage.removeItem('hasStartedConsultation');
    setHasStartedConsultation(false);
    setIsUserRegistered(false);
    
    // Clear any other stored data that might exist
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('consultation') || key.startsWith('health') || key.startsWith('bundle')) {
        localStorage.removeItem(key);
      }
    });

    trackEvent('in_store_session_reset');
    
    // Redirect to in-store landing page
    window.location.href = '/in-store';
  };

  return (
    <InStoreContext.Provider
      value={{
        isInStoreMode,
        isUserRegistered,
        hasStartedConsultation,
        clearInStoreMode,
        resetInStoreSession
      }}
    >
      {children}
    </InStoreContext.Provider>
  );
}; 